import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbsenceOptionUnit } from '@app/enums';
import { DecimalToDurationFormatPipe } from '@app/pipes/decimal-to-duration-format.pipe';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { AbsenteeOptionModel } from '@app/reducers/orm/absentee-option/absentee-option.model';
import { getAllAbsenteeOptions } from '@app/reducers/orm/absentee-option/absentee-option.service';
import { TimeDistributionAbsenceItem, TimeDistributionResponse } from '@app/reducers/orm/employee/employee.model';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { AbsenceService } from '@reducers/orm/absence/absence.service';
import { CorrectionService } from '@reducers/orm/correction/correction.service';
import { EmployeeService } from '@reducers/orm/employee/employee.service';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { HorizontalStackedBarChartComponent, StackedBarChartItem } from '@sb/ui/horizontal-stacked-bar-chart';
import { merge } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-type-distribution',
  templateUrl: './absence-type-distribution.component.html',
  imports: [TranslatePipe, HorizontalStackedBarChartComponent, LoadingStateComponent],
  providers: [LocaleDatePipe, DecimalToDurationFormatPipe],
})
export class AbsenceTypeDistributionComponent implements OnChanges {
  private store = inject<Store<AppState>>(Store);
  private employeeService = inject(EmployeeService);
  private datePipe = inject(LocaleDatePipe);
  private decimalToDurationFormat = inject(DecimalToDurationFormatPipe);
  private translate = inject(TranslateService);
  private correctionService = inject(CorrectionService);
  private absenceService = inject(AbsenceService);
  private destroyRef = inject(DestroyRef);

  employeeId = input<string>();
  year = input<string>();

  timeDistributionItems: StackedBarChartItem[];
  startDate: string;
  endDate: string;
  absenteeOptions: AbsenteeOptionModel[];
  loading = false;

  private currentYear = new Date().getFullYear().toString();

  constructor() {
    this.store
      .select(getAllAbsenteeOptions)
      .pipe(takeUntilDestroyed())
      .subscribe((absenteeOptions) => {
        this.absenteeOptions = absenteeOptions;
      });

    merge(this.correctionService.listenToChanges(), this.absenceService.listenToChanges())
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.getData();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const year = this.year();

    if (changes['employeeId'] || changes['year']) {
      this.getData();
    }

    if (changes['year']) {
      this.startDate = this.datePipe.transform(`${year}-01-01`, 'compact');
      this.endDate =
        year === this.currentYear
          ? this.translate.instant('Today')
          : this.datePipe.transform(`${year}-12-31`, 'compact');
    }
  }

  private getData() {
    this.loading = true;
    this.employeeService
      .getTimeDistribution(this.employeeId(), this.year())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: TimeDistributionResponse) => {
          if (data) {
            this.timeDistributionItems = [
              ...this.getAbsenceTimeDistributionItems(data.absences),
              this.getWorkedHoursDistributionItem(data),
            ];
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private getWorkedHoursDistributionItem(data: TimeDistributionResponse): StackedBarChartItem {
    const workedHours = this.decimalToDurationFormat.transform(data.workedHours, AbsenceOptionUnit.HOURS);
    return {
      id: 'time',
      label: this.translate.instant('Hours worked'),
      color: 'var(--colors-background-neutral-subtle)',
      percentage: data.workedHoursPercentage,
      tooltipText: this.getTooltipText(workedHours, data.workedHoursPercentage),
    };
  }

  private getAbsenceTimeDistributionItems(absences: TimeDistributionAbsenceItem[]): StackedBarChartItem[] {
    return absences.map((absence) => {
      const absenceType = this.getAbsenceType(absence.absenceTypeId);
      const absenceValue = this.decimalToDurationFormat.transform(absence.value, absence.valueUnit);

      return {
        id: absence.absenceTypeId,
        label: absenceType.option,
        color: absenceType.color,
        percentage: absence.percentage,
        tooltipText: this.getTooltipText(absenceValue, absence.percentage),
      };
    });
  }

  private getTooltipText(value: string, percentage: number) {
    const parsedPercentage = percentage && percentage < 1 ? '<1' : Math.floor(percentage);

    return `${value} (${parsedPercentage}%)`;
  }

  private getAbsenceType(id: string): AbsenteeOptionModel {
    return this.absenteeOptions.find((absence) => absence.id === id);
  }
}
